import React, {useEffect, useState} from 'react';
import {find, identity, sortBy, without} from 'lodash';
import {Box} from '@mui/material';
import {useBoolean} from 'src/hooks/useBoolean';
import Table from 'src/components/SimpleTable';
import tableStyles from 'src/components/SimpleTable/body.module.scss';
import Loading from '../Loading';
import Form from './Form';
import Row from './Row';
import styles from './styles.module.scss';
import FieldComponent from 'src/components/FieldComponent';
import InfoTooltip from 'shared/components/info-tooltip';
import {ColumnSettings, CompleteColumnSettings, ItemValue, PersistedItemValue} from './types';
import Button from '../../../components/button';
import Yup from '../../../utils/yup';

const DefaultFormField = (props: {column: CompleteColumnSettings}): React.ReactElement => (
  <FieldComponent name={props.column.name} type={props.column.type} required withLabel={false} />
);

const defaultColumn: Partial<ColumnSettings> = {
  sortable: false,
  renderValue: identity,
  FormField: DefaultFormField,
  editable: true,
  type: 'text',
  hidden: false,
};

export interface EditableTableProps {
  createOrUpdate: (item: ItemValue) => Promise<PersistedItemValue>;
  destroy: (id: string | undefined) => Promise<void>;
  index: () => Promise<PersistedItemValue[]>;
  defaultSortKey: string;
  columns: ColumnSettings[];
  identifier: string;
  defaultValues?: ItemValue;
  disableShimmerOnReload?: boolean;
  apiFieldMap?: Record<string, string>;
  allowBulkEdit?: boolean;
  disableUpdating?: boolean;
  disableAdding?: boolean;
  disableDeleting?: boolean;
  inContainer?: boolean;
  formSchema?: Yup.ObjectSchema;
  confirmDelete?: (item: PersistedItemValue) => Promise<boolean>;
  afterCreate?: (item: PersistedItemValue) => void;
}

const EditableTable = (props: EditableTableProps): React.ReactElement => {
  const columnsWithDefaultValues = props.columns.map((column) => ({
    ...defaultColumn,
    ...column,
  })) as CompleteColumnSettings[];

  const [items, setItems] = useState<PersistedItemValue[]>([]);
  const loading = useBoolean(true);
  const initialLoad = useBoolean(false);
  const bulkEdit = useBoolean(false);

  const [sortByKey, setSortByKey] = useState(props.defaultSortKey);
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | 'none'>(
    'ascending'
  );

  const sortFunction = (column) => (): void => {
    if (sortByKey === column) {
      setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
    } else {
      setSortDirection('ascending');
      setSortByKey(column);
    }
  };

  const removeItem = (id): void => {
    const existingItem: PersistedItemValue | undefined = find(items, {id});

    if (existingItem) {
      setItems(without(items, existingItem));
    }
  };

  const addItem = (item: PersistedItemValue): void => setItems([...items, item]);

  const updateItem = (updatedItem: PersistedItemValue): void => {
    setItems((previousItems) => {
      const updatedItems: PersistedItemValue[] = [];

      previousItems.forEach((existingItem): void => {
        if (existingItem.id === updatedItem.id) {
          updatedItems.push(updatedItem);
        } else {
          updatedItems.push(existingItem);
        }
      });

      return updatedItems;
    });
  };

  const createOrUpdate = (values: ItemValue | PersistedItemValue): Promise<void> =>
    props.createOrUpdate(values).then((response: PersistedItemValue): void => {
      if (values.id) {
        updateItem({...values, id: values.id + ''});
      } else {
        addItem(response);
        props.afterCreate?.(response);
      }
    });

  const columns = columnsWithDefaultValues
    .filter((column) => !column.hidden)
    .map(({title, name, sortable, tooltip}) => ({
      value: (
        <>
          {title}
          {tooltip && <InfoTooltip white>{tooltip}</InfoTooltip>}
        </>
      ),
      sort: sortable,
      onSort: sortable ? sortFunction(name) : undefined,
      sortDirection: sortByKey === name ? sortDirection : undefined,
    }));

  useEffect(() => {
    loading.setTrue();
    props.index().then(setItems).then(loading.setFalse).then(initialLoad.setTrue);
  }, [props.identifier]);

  const sortedItems = sortBy(items, [sortByKey]);

  if (sortDirection === 'descending') {
    sortedItems.reverse();
  }

  const displayLoading = loading.value && (!props.disableShimmerOnReload || !initialLoad.value);

  return (
    <div data-testid="editable-table" className="editable-table">
      {displayLoading && <Loading />}
      {!displayLoading && (
        <Box mt={5}>
          {!props.disableAdding && (
            <Table inContainer={props.inContainer}>
              <thead>
                <tr>
                  {columnsWithDefaultValues
                    .filter((column) => column.editable || column.editableOnCreate)
                    .map(({title}) => (
                      <th key={title} className={styles.formCell}>
                        {title}
                      </th>
                    ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                <Form
                  showButton
                  createOrUpdate={createOrUpdate}
                  apiFieldMap={props.apiFieldMap}
                  columns={columnsWithDefaultValues}
                  values={props.defaultValues || {}}
                  renderNonEditableFields={false}
                  inContainer={props.inContainer}
                  formSchema={props.formSchema}
                />
              </tbody>
            </Table>
          )}
          {props.allowBulkEdit && (
            <Box>
              <Button onClick={bulkEdit.toggle} size="small">
                {bulkEdit.value ? 'Save' : 'Bulk edit'}
              </Button>
            </Box>
          )}
          <Box mt={2} mb={4}>
            <Table>
              <Table.Header centerText columns={columns} />
              <tbody>
                {!items.length && (
                  <tr>
                    <td colSpan={3} className={tableStyles.empty}>
                      No items yet.
                    </td>
                  </tr>
                )}
                {sortedItems.map((item) => (
                  <Row
                    key={item.id}
                    disableUpdating={props.disableUpdating}
                    disableDeleting={props.disableDeleting}
                    apiFieldMap={props.apiFieldMap}
                    columns={columnsWithDefaultValues.filter((column) => !column.hidden)}
                    destroy={async (id: string): Promise<void> => {
                      if (props.confirmDelete) {
                        const confirmed = await props.confirmDelete(item);
                        if (!confirmed) return;
                      }
                      await props.destroy(id);
                      removeItem(id);
                    }}
                    createOrUpdate={createOrUpdate}
                    values={item}
                    forceEdit={bulkEdit.value}
                  />
                ))}
              </tbody>
            </Table>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default EditableTable;
